import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useCookiesContext } from '../../contexts/CookiesContext'
import { getClientCampaings } from '../../services/adv'
import AppvizerMenu from './AppvizerMenu'
import AdvertiserMenu from './AdvertiserMenu'
import CampaignsMenu from './CampaignsMenu'
import PublisherSection from './PublisherSection'
import FinancesMenu from './FinancesMenu'
import { advertiserSidebarLabels } from '../../data/Menu'
import { publisherDashboardMenu, publisherCustomerMenu, publisherDefaultFinanceMenu, publisherInventoryMenu } from '../../data/Menu'
import { WithState } from '../../hooks/withState'
import { getQueueItemsUnderReview } from '@helpers/get-queue-items-under-review'
import { cookiesKeyNames } from '@revbox/common-fe'
import { useLocation } from 'react-router-dom'

const SidebarMenu = React.memo(({ userAccesses, advertiserId, publisherId, user, onUpdateSize }) => {
    const { getLanguageAbbr } = useCookiesContext()
    const language = getLanguageAbbr()
    const isAppvizer = user?.owner_id === 73
    const [campaigns, setCampaigns] = useState([])
    const [reviewsRecords, setReviewsRecords] = useState(0)
    const location = useLocation()
    const { updateCookiesProperty } = useCookiesContext()
    useLayoutEffect(() => {
        let fetchedCampaigns = []
        if (!advertiserId) {
            setCampaigns([])
            return
        }
        getClientCampaings(advertiserId)
            .then(res => {
                if (res?.success) {
                    fetchedCampaigns = res.data
                }
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                setCampaigns(fetchedCampaigns)
            })
    }, [advertiserId])

    let sidebarLogo

    const addRoleSign = advertiserId => {
        let content = 'PUB'

        if (advertiserId) {
            content = 'ADV'
        }

        const existingRoleSign = document.querySelector('.role-sign')
        if (existingRoleSign) {
            existingRoleSign.remove()
        }

        const roleSign = document.createElement('div')
        roleSign.className = 'role-sign'
        roleSign.textContent = content

        sidebarLogo.appendChild(roleSign)
    }

    useEffect(() => {
        sidebarLogo = document.querySelector('.sidebar-logo')

        addRoleSign(advertiserId)
    }, [advertiserId])

    const toggleMenu = e => {
        e.preventDefault()

        let parent = e.target.closest('.nav-group')
        parent.classList.toggle('show')

        onUpdateSize()
    }

    const hasAccess = type => userAccesses?.includes(type)

    const appvizerMenuCondition = advertiserId && isAppvizer
    const advertiserMenuCondition = advertiserId && hasAccess('dashboards_access')
    const campaignsMenuCondition = advertiserId && hasAccess('advertiser_access') && campaigns.length > 0
    const dashboardPublisherCondition = publisherId && !advertiserId && hasAccess('dashboards_access')
    const inventoryPublisherCondition = publisherId && !advertiserId && hasAccess('inventory_access')
    const customersPublisherCondition = publisherId && !advertiserId && hasAccess('customers_access')
    const financesPublisherCondition = publisherId && !advertiserId && hasAccess('finance_access')
    const financesAdvertiserCondition = advertiserId && hasAccess('finance_access')
    const isApplicationsAvailable = useMemo(() => {
        return publisherId === 34
    }, [publisherId])


    useEffect(() => {
        const fetchQueueItemsUnderReview = async () => {
            const response = await getQueueItemsUnderReview()
            if (response?.success) {
                setReviewsRecords(response.reviewRecords)
            }
        }
        fetchQueueItemsUnderReview()
        if (location.pathname.includes('/adv/')) {
            updateCookiesProperty(cookiesKeyNames.sidebarSkin, 'advertiser', advertiserId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])


    return (
        <React.Fragment>
            {appvizerMenuCondition && (
                <AppvizerMenu
                    toggleMenu={toggleMenu}
                    advertiserId={advertiserId}
                    onUpdateSize={onUpdateSize}
                    title={advertiserSidebarLabels.PlatformAccess[language]}
                    language={language}
                />
            )}
            {advertiserMenuCondition && (
                <AdvertiserMenu
                    toggleMenu={toggleMenu}
                    onUpdateSize={onUpdateSize}
                    advertiserId={advertiserId}
                    title={advertiserSidebarLabels.Dashboard[language]}
                    language={language}
                />
            )}
            {campaignsMenuCondition && (
                <WithState initialState={true}>
                    {(isCampaignsOpened, setIsCampaignsOpened) => {
                        const toggleCampaigns = e => {
                            e.preventDefault()
                            setIsCampaignsOpened(!isCampaignsOpened)
                            onUpdateSize()
                        }
                        return (
                            <CampaignsMenu
                                isCampaignsOpened={isCampaignsOpened}
                                toggleCampaigns={toggleCampaigns}
                                menuName={advertiserSidebarLabels.Campaigns[language]}
                                campaigns={campaigns}
                                advertiserId={advertiserId}
                                onUpdateSize={onUpdateSize}
                                language={language}
                            />
                        )
                    }}
                </WithState>
            )}
            {dashboardPublisherCondition && (
                <PublisherSection
                    label="Dashboards"
                    language={language}
                    toggleMenu={toggleMenu}
                    advertiserId={advertiserId}
                    onUpdateSize={onUpdateSize}
                    menu={publisherDashboardMenu}
                />
            )}
            {inventoryPublisherCondition && (
                <PublisherSection
                    label="Inventory"
                    language={language}
                    toggleMenu={toggleMenu}
                    menu={publisherInventoryMenu}
                    advertiserId={advertiserId}
                    onUpdateSize={onUpdateSize}
                    reviewsRecords={reviewsRecords}
                />
            )}
            {customersPublisherCondition && (
                <PublisherSection
                    label="Customers"
                    isApplicationsAvailable={isApplicationsAvailable}
                    language={language}
                    toggleMenu={toggleMenu}
                    menu={publisherCustomerMenu}
                    advertiserId={advertiserId}
                    onUpdateSize={onUpdateSize}
                />
            )}
            {financesPublisherCondition && (
                <PublisherSection
                    label="Finances"
                    language={language}
                    toggleMenu={toggleMenu}
                    menu={publisherDefaultFinanceMenu}
                    advertiserId={advertiserId}
                    onUpdateSize={onUpdateSize}
                />
            )}
            {financesAdvertiserCondition && (
                <FinancesMenu
                    advertiserId={advertiserId}
                    publisherId={publisherId}
                    hasAccess={hasAccess}
                    toggleMenu={toggleMenu}
                    onUpdateSize={onUpdateSize}
                    menuName={advertiserSidebarLabels.Finances[language]}
                />
            )}
        </React.Fragment>
    )
})

export default SidebarMenu
